import './lib/applySafeAreaPlatformClass'
import './lib/authRecoveryBoot'
import { StrictMode, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import './index.css'
import Login from './pages/Login.tsx'
import Signup from './pages/Signup.tsx'
import ResetPassword from './pages/ResetPassword.tsx'
import RoleSelector from './pages/RoleSelector.tsx'
import LanguagePicker from './pages/LanguagePicker.tsx'
import StaffLogin from './pages/StaffLogin.tsx'
import { StaffApp } from './layouts/StaffApp.tsx'
import { BuyerApp } from './layouts/BuyerApp.tsx'
import { InspectorApp } from './layouts/InspectorApp.tsx'
import { AuthProvider, useAuth } from './context'
import { homeRouteForRole, isPortalRole, UNRESOLVED_ROLE } from './lib/roles'
import { safeStorage } from './lib/safe-storage'
import { ErrorBoundary } from './components/ErrorBoundary.tsx'
import { recoverFromStaleChunk } from './lib/recoverStaleChunk'
import { AuthUrlBootstrap } from './components/AuthUrlBootstrap.tsx'
import { ModuleLoader } from './components/ui/ModuleLoader.tsx'
import { useAndroidBackButton } from './hooks/useAndroidBackButton'
import { Toaster } from 'sonner'

/** Portals Sonner to `document.body` so `position:fixed` is viewport-locked (not inside transformed scroll roots). */
function FixedSonnerToaster () {
  return createPortal(
    <Toaster
      position="top-right"
      offset={{ top: '0.75rem', right: '0.75rem' }}
      richColors
      closeButton
      duration={4500}
      gap={10}
      toastOptions={{
        classNames: {
          toast: 'railsahayak-toast',
          title: 'railsahayak-toast-title',
          description: 'railsahayak-toast-desc'
        }
      }}
    />,
    document.body
  )
}

// Lazy-loaded pages — split into role-based bundles
const App = lazy(() => import('./App.tsx'))
const StaffHome = lazy(() => import('./pages/StaffHome.tsx'))
const StaffScan = lazy(() => import('./pages/StaffScan.tsx'))
const StaffManualSelect = lazy(() => import('./pages/StaffManualSelect.tsx'))
const StaffCapture = lazy(() => import('./pages/StaffCapture.tsx'))
const StaffSubmit = lazy(() => import('./pages/StaffSubmit.tsx'))
const StaffTodaySubmissions = lazy(() => import('./pages/StaffTodaySubmissions.tsx'))
const StaffCyclesDue = lazy(() => import('./pages/StaffCyclesDue.tsx'))
const StaffAlerts = lazy(() => import('./pages/StaffAlerts.tsx'))
const StaffProfile = lazy(() => import('./pages/StaffProfile.tsx'))
const StaffCompliance = lazy(() => import('./pages/StaffCompliance.tsx'))
const StaffComplaints = lazy(() => import('./pages/StaffComplaints.tsx'))
const BuyerHome = lazy(() => import('./pages/BuyerHome.tsx'))
const BuyerTaskList = lazy(() => import('./pages/BuyerTaskList.tsx'))
const BuyerEvaluate = lazy(() => import('./pages/BuyerEvaluate.tsx'))
const BuyerHistory = lazy(() => import('./pages/BuyerHistory.tsx'))
const BuyerAssetBrowser = lazy(() => import('./pages/BuyerAssetBrowser.tsx'))
const BuyerActivityView = lazy(() => import('./pages/BuyerActivityView.tsx'))
const ChiSupervisors = lazy(() => import('./pages/ChiSupervisors.tsx'))
const ChiSendAlert = lazy(() => import('./pages/ChiSendAlert.tsx'))
const ChiAlertsReview = lazy(() => import('./pages/ChiAlertsReview.tsx'))
const ChiDeployment = lazy(() => import('./pages/ChiDeployment.tsx'))
const ChiDeploymentDaily = lazy(() => import('./pages/ChiDeploymentDaily.tsx'))
const ChiReports = lazy(() => import('./pages/ChiReports.tsx'))
const ChiSupplyReceive = lazy(() => import('./pages/ChiSupplyReceive.tsx'))
const ChiSupplyReceiveForm = lazy(() => import('./pages/ChiSupplyReceiveForm.tsx'))
const BuyerQueue = lazy(() => import('./pages/BuyerQueue.tsx'))
const BuyerComplaints = lazy(() => import('./pages/BuyerComplaints.tsx'))
const InspectorHome = lazy(() => import('./pages/InspectorHome.tsx'))
const InspectorQueue = lazy(() => import('./pages/InspectorQueue.tsx'))
const InspectorReview = lazy(() => import('./pages/InspectorReview.tsx'))
const InspectorHistory = lazy(() => import('./pages/InspectorHistory.tsx'))
const InspectorFindings = lazy(() => import('./pages/InspectorFindings.tsx'))
const FeedbackForm = lazy(() => import('./pages/FeedbackForm.tsx'))
const ReportViewPage = lazy(() => import('./pages/ReportViewPage.tsx'))
const PublicComplaint = lazy(() => import('./pages/PublicComplaint.tsx'))

/** Paths that must work without login (QR deep-links). Also checked inside ProtectedRoute when `/*` catches them. */
const PUBLIC_NO_AUTH_ROUTES: Record<string, React.LazyExoticComponent<() => React.ReactElement>> = {
  '/complaint': PublicComplaint,
  '/feedback': FeedbackForm,
}

function PublicNoAuthPage ({ path }: { path: string }) {
  const Page = PUBLIC_NO_AUTH_ROUTES[path]
  if (!Page) return null
  return (
    <Suspense fallback={<LazyFallback />}>
      <Page />
    </Suspense>
  )
}


function LazyFallback() {
  return <ModuleLoader variant="screen" tone="dark" label="Loading" />
}

/** Full-screen boot state shared by every route gate. */
function BootScreen() {
  return <ModuleLoader variant="screen" tone="dark" label="Starting RailSahayak" />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (PUBLIC_NO_AUTH_ROUTES[location.pathname]) {
    return <PublicNoAuthPage path={location.pathname} />
  }

  if (loading) {
    return <ModuleLoader variant="screen" tone="dark" label="Loading" />
  }

  if (!user) {
    return <Navigate to="/welcome" replace />
  }

  return <>{children}</>
}

/**
 * Gate for the admin console catch-all.
 *
 * The staff redirect used to live inside `App` as an early `return` sitting
 * between hook 8 and hook 21, so any role change after mount threw
 * "Rendered fewer hooks than expected" and unmounted the tree. Deciding here
 * means `App` never mounts for a portal role at all.
 */
function roleStillLoading (
  user: unknown,
  loading: boolean,
  profile: unknown,
  profileDegraded: boolean
): boolean {
  if (loading) return true
  // Fresh login: session exists before the profile/role has loaded. Do not
  // send staff to the admin console (`/`) under the unresolved contractor role.
  return Boolean(user) && profile == null && !profileDegraded
}

function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, role, profile, profileDegraded } = useAuth()
  const location = useLocation()

  if (PUBLIC_NO_AUTH_ROUTES[location.pathname]) {
    return <PublicNoAuthPage path={location.pathname} />
  }

  if (roleStillLoading(user, loading, profile, profileDegraded)) return <BootScreen />

  if (!user) {
    return <Navigate to="/welcome" replace />
  }

  if (isPortalRole(role)) {
    return <Navigate to={homeRouteForRole(role)} replace />
  }

  return <>{children}</>
}

function StaffProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, role, profile, profileDegraded } = useAuth()

  if (roleStillLoading(user, loading, profile, profileDegraded)) {
    return <ModuleLoader variant="screen" tone="dark" label="Loading" />
  }

  if (!user) {
    return <Navigate to="/staff/login" replace />
  }

  if (role !== 'contractor_staff' && role !== 'admin' && role !== 'super_admin') {
    return <Navigate to={homeRouteForRole(role)} replace />
  }

  return <StaffApp>{children}</StaffApp>
}

function BuyerProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, role, profile, profileDegraded } = useAuth()

  if (roleStillLoading(user, loading, profile, profileDegraded)) {
    return <ModuleLoader variant="screen" tone="dark" label="Loading" />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (role !== 'buyer' && role !== 'admin' && role !== 'super_admin') {
    return <Navigate to={homeRouteForRole(role)} replace />
  }

  return <BuyerApp>{children}</BuyerApp>
}

function InspectorProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, role, profile, profileDegraded } = useAuth()

  if (roleStillLoading(user, loading, profile, profileDegraded)) {
    return <ModuleLoader variant="screen" tone="dark" label="Loading" />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (role !== 'inspector' && role !== 'admin' && role !== 'super_admin') {
    return <Navigate to={homeRouteForRole(role)} replace />
  }

  return <InspectorApp>{children}</InspectorApp>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, role, profile, profileDegraded } = useAuth()

  if (roleStillLoading(user, loading, profile, profileDegraded) || (user && role === UNRESOLVED_ROLE && !profileDegraded)) {
    return <ModuleLoader variant="screen" tone="dark" label="Loading" />
  }

  if (user) {
    return <Navigate to={homeRouteForRole(role)} replace />
  }

  if (!safeStorage.get('lang_selected')) {
    return <Navigate to="/language" replace />
  }

  return <>{children}</>
}

/** Registers the Android hardware back button → in-app navigation. */
function BackButtonHandler() {
  useAndroidBackButton()
  return null
}

function AppRouter() {
  return (
    <BrowserRouter>
      <AuthUrlBootstrap>
        <BackButtonHandler />
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/language" element={<LanguagePicker />} />
        <Route path="/welcome" element={
          <PublicRoute>
            <RoleSelector />
          </PublicRoute>
        } />
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/staff/login" element={
          <PublicRoute>
            <StaffLogin />
          </PublicRoute>
        } />
        <Route path="/signup" element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        } />
        <Route path="/reset-password" element={
          <ResetPassword />
        } />
        <Route path="/feedback" element={<FeedbackForm />} />
        <Route path="/complaint" element={<PublicComplaint />} />
        <Route path="/reporting/view" element={
          <ProtectedRoute>
            <ReportViewPage />
          </ProtectedRoute>
        } />

        {/* Staff routes */}
        <Route path="/staff/home" element={
          <StaffProtectedRoute><StaffHome /></StaffProtectedRoute>
        } />
        <Route path="/staff/scan" element={
          <StaffProtectedRoute><StaffScan /></StaffProtectedRoute>
        } />
        <Route path="/staff/select" element={
          <StaffProtectedRoute><StaffManualSelect /></StaffProtectedRoute>
        } />
        <Route path="/staff/capture" element={
          <StaffProtectedRoute><StaffCapture /></StaffProtectedRoute>
        } />
        <Route path="/staff/submit" element={
          <StaffProtectedRoute><StaffSubmit /></StaffProtectedRoute>
        } />
        <Route path="/staff/alerts" element={
          <StaffProtectedRoute><StaffAlerts /></StaffProtectedRoute>
        } />
        <Route path="/staff/profile" element={
          <StaffProtectedRoute><StaffProfile /></StaffProtectedRoute>
        } />
        <Route path="/staff/compliance" element={
          <StaffProtectedRoute><StaffCompliance /></StaffProtectedRoute>
        } />
        <Route path="/staff/complaints" element={
          <StaffProtectedRoute><StaffComplaints /></StaffProtectedRoute>
        } />
        <Route path="/staff/today" element={
          <StaffProtectedRoute><StaffTodaySubmissions /></StaffProtectedRoute>
        } />
        <Route path="/staff/cycles" element={
          <StaffProtectedRoute><StaffCyclesDue /></StaffProtectedRoute>
        } />

        {/* Buyer (CHI) mobile app routes */}
        <Route path="/buyer/home" element={
          <BuyerProtectedRoute><BuyerHome /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/tasks" element={
          <BuyerProtectedRoute><BuyerTaskList /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/evaluate" element={
          <BuyerProtectedRoute><BuyerEvaluate /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/evaluate/:submissionId" element={
          <BuyerProtectedRoute><BuyerEvaluate /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/quick-score" element={
          <BuyerProtectedRoute><Navigate to="/buyer/tasks" replace /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/history" element={
          <BuyerProtectedRoute><BuyerHistory /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/assets" element={
          <BuyerProtectedRoute><BuyerAssetBrowser /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/activity" element={
          <BuyerProtectedRoute><BuyerActivityView /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/review" element={
          <BuyerProtectedRoute><Navigate to="/buyer/tasks" replace /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/review/:submissionId" element={
          <BuyerProtectedRoute><Navigate to="/buyer/tasks" replace /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/supervisors" element={
          <BuyerProtectedRoute><ChiSupervisors /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/supervisors/new" element={
          <BuyerProtectedRoute><Navigate to="/buyer/supervisors" replace /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/send-alert" element={
          <BuyerProtectedRoute><ChiSendAlert /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/alerts-review" element={
          <BuyerProtectedRoute><ChiAlertsReview /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/queue" element={
          <BuyerProtectedRoute><BuyerQueue /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/complaints" element={
          <BuyerProtectedRoute><BuyerComplaints /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/deployment" element={
          <BuyerProtectedRoute><ChiDeployment /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/deployment/daily" element={
          <BuyerProtectedRoute><ChiDeploymentDaily /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/reports" element={
          <BuyerProtectedRoute><ChiReports /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/supplies/receive" element={
          <BuyerProtectedRoute><ChiSupplyReceive /></BuyerProtectedRoute>
        } />
        <Route path="/buyer/supplies/receive/form" element={
          <BuyerProtectedRoute><ChiSupplyReceiveForm /></BuyerProtectedRoute>
        } />

        {/* Inspector mobile app routes */}
        <Route path="/inspector/home" element={
          <InspectorProtectedRoute><InspectorHome /></InspectorProtectedRoute>
        } />
        <Route path="/inspector/queue" element={
          <InspectorProtectedRoute><InspectorQueue /></InspectorProtectedRoute>
        } />
        <Route path="/inspector/review" element={
          <InspectorProtectedRoute><InspectorReview /></InspectorProtectedRoute>
        } />
        <Route path="/inspector/review/:inspectionId" element={
          <InspectorProtectedRoute><InspectorReview /></InspectorProtectedRoute>
        } />
        <Route path="/inspector/history" element={
          <InspectorProtectedRoute><InspectorHistory /></InspectorProtectedRoute>
        } />
        <Route path="/inspector/findings" element={
          <InspectorProtectedRoute><InspectorFindings /></InspectorProtectedRoute>
        } />

        {/* Admin/Contractor app */}
        <Route path="/*" element={
          <AdminProtectedRoute>
            <App />
          </AdminProtectedRoute>
        } />
      </Routes>
      </Suspense>
      </AuthUrlBootstrap>
    </BrowserRouter>
  )
}

// Set up notification channels + foreground alert sound on native startup,
// independent of the Settings push opt-in (channel creation needs no permission).
// Also tag <html> so CSS can apply Android status-bar safe-area fallbacks.
void (async () => {
  try {
    const { platform } = await import('./services/platform')
    if (platform.isNative()) {
      document.documentElement.classList.add('is-native')
      if (platform.isAndroid()) {
        document.documentElement.classList.add('is-android')
      }
      if (platform.isIOS()) {
        document.documentElement.classList.add('is-ios')
      }
      const { nativePushNotifications } = await import('./services/nativePushNotifications')
      await nativePushNotifications.initSound()
    }
  } catch (e) {
    console.warn('[sound] startup init skipped:', e)
  }
})()

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  void recoverFromStaleChunk()
})

try {
  sessionStorage.removeItem('rs-stale-chunk-reload')
} catch {
  /* ignore */
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <AppRouter />
        <FixedSonnerToaster />
      </AuthProvider>
    </ErrorBoundary>
  </StrictMode>,
)
